import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Login.css";
import { getStoredData } from "../../api/fetchData.jsx";
import { getAllPossiblePermissions } from "../../utils/permissions.js";
import {
  faEye,
  faEyeSlash,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Login = () => {
  const [username, setUsername] = useState(
    localStorage.getItem("savedEmail") || "",
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem("rememberMe") === "true",
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const data = getStoredData();
  const fields = data?.[2] || {};
  const stored = getStoredData();
  const baseApi = stored?.[0]?.Api || "https://lttsbackend.axiomos.in";

  useEffect(() => {
    const expirationTime = localStorage.getItem("expirationTime");
    if (expirationTime && new Date().getTime() > Number(expirationTime)) {
      localStorage.clear();
    }
  }, []);

  const findMissingPermissions = (userPerms) => {
    const allPermissions = getAllPossiblePermissions();
    const uniquePerms = [...new Set(allPermissions)];
    return uniquePerms.filter((perm) => !userPerms.includes(perm));
  };

  const fetchAppVersion = async () => {
    try {
      const res = await axios.get("https://lttsbackend.axiomos.in/apk/latest");
      return res.data?.version || null;
    } catch (err) {
      console.error("Failed to fetch app version:", err);
      return null;
    }
  };

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (isSubmitting) return;

    if (!username.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      const appVersion = await fetchAppVersion();
      localStorage.setItem("appVersion", appVersion || "unknown");

      const response = await axios.post(
        `${baseApi}/user/login`,
        {
          emailId: username.trim(),
          password,
          appVersion,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.data.token && response.data.user) {
        const { token, user } = response.data;
        const allowedRoles = [
          "Admin",
          "Client",
          "Project Manager",
          "Management",
        ];
        const roleName = user?.role?.roleName;

        if (!allowedRoles.includes(roleName)) {
          setError("You do not have permission to access this panel.");
          return;
        }

        const expirationDuration = rememberMe
          ? 7 * 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000;

        localStorage.setItem("authenticated", "true");
        localStorage.setItem("token", token);
        localStorage.setItem(
          "expirationTime",
          String(new Date().getTime() + expirationDuration),
        );

        if (rememberMe) {
          localStorage.setItem("savedEmail", username.trim());
        } else {
          localStorage.removeItem("savedEmail");
        }

        localStorage.setItem("rememberMe", String(rememberMe));

        const {
          _id,
          userId,
          name,
          emailId,
          phone,
          address,
          designation,
          role,
          project = [],
          isActive,
          isApproved,
          photo,
          createdAt,
          updatedAt,
        } = user;

        const userDetailsToStore = {
          _id,
          userId,
          name,
          emailId,
          phone,
          address,
          designation: designation || null,
          role: role?._id
            ? {
                _id: role._id,
                roleName: role.roleName,
                permissions: role.permissions || [],
              }
            : role || null,
          project: Array.isArray(project)
            ? project.map((p) => ({
                _id: p._id,
                projectName: p.projectName,
                company: p.company || null,
                description: p.description || null,
                siteLocation: p.siteLocation || null,
                projectId: p.projectId || null,
                status: p.status || null,
                startDate: p.startDate || null,
                endDate: p.endDate || null,
              }))
            : [],
          isActive: isActive !== false,
          isApproved: !!isApproved,
          photo: photo || null,
          createdAt,
          updatedAt,
        };

        localStorage.setItem("userDetails", JSON.stringify(userDetailsToStore));
        localStorage.setItem(
          "permissions",
          JSON.stringify(role?.permissions || []),
        );
        localStorage.setItem(
          "fieldsAccess",
          JSON.stringify(role?.fieldsAccess || []),
        );

        navigate("/");
      } else {
        setError(
          fields?.errorMessagesinvalidCredentials || "Invalid credentials",
        );
      }
    } catch (err) {
      console.error("Login Error:", err);
      setError(
        err.response?.data?.message ||
          fields?.errorMessagesinvalidCredentials ||
          "Invalid credentials",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page auth-theme">
      <div className="login-container">
        <form className="login-section" onSubmit={handleLogin}>
          <div className="login-brand-row">
            <img
              src={fields?.headerlogo || "/imagesLogin/jkumar_logo_white.png"}
              alt="J Kumar"
              className="login-logo"
            />
            <span className="login-security-pill">
              <FontAwesomeIcon icon={faShieldHalved} />
              Secure Panel
            </span>
          </div>

          <div className="login-copy">
            <p className="login-eyebrow">Operations Command Center</p>
            <h2 className="login-heading">
              {fields?.headerwelcomeMessage || "Welcome Back"}
            </h2>
            <p className="login-subheading">
              {fields?.headersubheading ||
                "Sign in to monitor safety, compliance, and site operations."}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-email">
              {fields?.formemailLabel || "Email address"}
            </label>
            <input
              id="login-email"
              type="email"
              className="form-input-login"
              placeholder={fields?.formemailPlaceholder || "name@company.com"}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">
              {fields?.formpasswordLabel || "Password"}
            </label>

            <div className="password-field">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                className="form-input-login"
                placeholder={
                  fields?.formpasswordPlaceholder || "Enter password"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />

              <button
                type="button"
                className="password-toggle-icon"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          <div className="login-BelowLinks">
            <label className="remember-me">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="checkbox-label">
                {fields?.formrememberMeLabel || "Remember me"}
              </span>
            </label>

            <button
              type="button"
              className="forgot-password"
              onClick={() => navigate("/reset-password")}
            >
              {fields?.formforgotPasswordText || "Forgot password?"}
            </button>
          </div>

          {error && <p className="error">{error}</p>}

          <button
            className="login-button"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in..." : fields?.buttonslogin || "Login"}
          </button>

          <p className="register-link">
            {fields?.linksregisterText || "Do not have an account?"}{" "}
            <a
              href={fields?.linksregisterHref || "/register"}
              className="register-link-text"
            >
              {fields?.linksregisterLinkText || "Register here"}
            </a>
          </p>
        </form>
      </div>

      <aside className="design-side" aria-hidden="true">
        <div className="design-overlay" />
        <div
          className="slide"
          style={{
            backgroundImage:
              "url(/imagesLogin/1761298262348-A10_PJS_9699.webp)",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761644455253-DJI_0028.webp)",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761744580051-ALWAR_ESIC.webp)",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761801048078-DSC_0232.webp)",
          }}
        />

        <div className="design-content">
          <span className="design-kicker">J Kumar Safety Operations</span>
          <h1>Built for faster decisions across every active site.</h1>
          <p>
            Track incidents, permits, audits, workforce activity, and compliance
            signals from one secure control panel.
          </p>

          <div className="design-stats">
            <div>
              <strong>24/7</strong>
              <span>Site visibility</span>
            </div>
            <div>
              <strong>Live</strong>
              <span>Operations data</span>
            </div>
            <div>
              <strong>Secure</strong>
              <span>Role access</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Login;
